import {formatDate} from '@cohort/shared/utils/format';
import TriggerCta from '@cohort/wallet/apps/TriggerCta';
import TriggerPickerModal from '@cohort/wallet/apps/TriggerPickerModal';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import useNotify from '@cohort/wallet/hooks/notify';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {JourneyStepTriggerWDto, JourneyStepWDto} from '@cohort/wallet-schemas/journeySteps';
import {LockSimple} from '@phosphor-icons/react';
import {CheckCircle} from '@phosphor-icons/react';
import {XCircle} from '@phosphor-icons/react';
import {Fragment, useState} from 'react';
import {useTranslation} from 'react-i18next';

const URL_TRIGGER_PARAM = 'selectedTriggerId';

function getSelectedTriggerFromUrl(
  journeyStepTriggers: Array<JourneyStepTriggerWDto>
): JourneyStepTriggerWDto | null {
  const params = new URLSearchParams(location.search);
  const urlSelectedTriggerId = params.get(URL_TRIGGER_PARAM);
  return journeyStepTriggers.find(trigger => trigger.id === urlSelectedTriggerId) ?? null;
}

function buildRedirectUrl(selectedTrigger: JourneyStepTriggerWDto): string {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set(URL_TRIGGER_PARAM, selectedTrigger.id);
  return currentUrl.toString();
}

type JourneyStepCtaProps = {
  journeyId: string;
  step: JourneyStepWDto;
  journeyParticipationId: string;
  isCampaignEnded: boolean;
};

const JourneyStepCta: React.FC<JourneyStepCtaProps> = ({
  journeyId,
  step,
  journeyParticipationId,
  isCampaignEnded,
}) => {
  const {accentColor} = useThemeContext();
  const {t} = useTranslation('components', {keyPrefix: 'space.journeyStepCta'});
  const urlSelectedTrigger = getSelectedTriggerFromUrl(step.triggers);
  const [triggerModalOpen, setTriggerModalOpen] = useState(urlSelectedTrigger ? true : false);
  const notify = useNotify();

  const [selectedTrigger, setSelectedTrigger] = useState<JourneyStepTriggerWDto | null>(
    urlSelectedTrigger
  );

  const notifyLockedReason = (
    reason: 'start-date' | 'pre-required-steps' | 'campaign-ended'
  ): void => {
    const {startDate} = step.orchestration;
    if (reason === 'start-date' && startDate) {
      notify('info', t('lockedStartDate', {date: formatDate(startDate)}));
    } else if (reason === 'pre-required-steps') {
      notify('info', t('lockedPreRequiredSteps'));
    } else if (reason === 'campaign-ended') {
      notify('info', t('lockedCampaignEnded'));
    }
  };

  if (step.isCompleted) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs font-semibold md:ml-10 md:mt-0">
        <CheckCircle
          className="h-5 w-5"
          style={{
            color: accentColor,
          }}
        />
        {t('stepCompleted')}
      </div>
    );
  }

  if (step.availability.locked === true || isCampaignEnded) {
    const reason =
      !isCampaignEnded && step.availability.locked === true
        ? step.availability.reason
        : 'campaign-ended';

    return (
      <div
        className="mt-3 flex items-center gap-2 text-xs font-semibold opacity-50 md:ml-10 md:mt-0"
        onClick={() => notifyLockedReason(reason)}
      >
        <div>
          <LockSimple
            className="h-5 w-5 hover:cursor-pointer"
            style={{
              color: accentColor,
            }}
          />
        </div>
      </div>
    );
  }

  if (step.triggers.length === 0) {
    return <Fragment />;
  }
  const noMoreAttempts = step.triggers.every(trigger => trigger.verificationAttemptsLeft === 0);

  if (noMoreAttempts) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs font-semibold md:ml-10 md:mt-0">
        <XCircle
          className="h-5 w-5"
          style={{
            color: accentColor,
          }}
        />
        {t('noMoreVerificationAttempts')}
      </div>
    );
  }

  if (step.triggers.length > 1) {
    return (
      <Fragment>
        <PrimaryButton
          size="small"
          type="button"
          text={t('complete')}
          tracking={{
            namespace: 'journeys.detail.complete',
            metadata: {
              journeyId,
              stepId: step.id,
              journeyParticipationId,
            },
          }}
          onClick={() => setTriggerModalOpen(true)}
        />
        {triggerModalOpen && (
          <TriggerPickerModal
            journeyId={journeyId}
            journeyParticipationId={journeyParticipationId}
            journeyStepId={step.id}
            triggers={step.triggers}
            selectedTrigger={selectedTrigger}
            setSelectedTrigger={setSelectedTrigger}
            buildRedirectUrl={buildRedirectUrl}
            onClose={() => setTriggerModalOpen(false)}
          />
        )}
      </Fragment>
    );
  }
  return (
    <div className="mt-3 md:mt-0">
      <TriggerCta
        journeyId={journeyId}
        journeyParticipationId={journeyParticipationId}
        journeyStepId={step.id}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        trigger={step.triggers[0]!}
        xpsRedirectUrl={window.location.href}
      />
    </div>
  );
};

export default JourneyStepCta;
